<template>
    <div
        v-if="!alertStorage[alertName] && isVisible"
        class="flex gap-3 h-100 w-full py-3 px-5"
        :class="{
            'bg-green-100': type === 'success',
            'border-green-300 border rounded': type === 'success' && floating,
            'bg-blue-100': type === 'info',
            'border-blue-300 border rounded': type === 'info' && floating,
            'bg-yellow-100': type === 'warning',
            'border-yellow-300 border rounded': type === 'warning' && floating,
            'bg-red-100': type === 'danger',
            'border-red-300 border rounded': type === 'danger' && floating,
            hidden: alertStorage[alertName],
        }"
    >
        <slot name="icon" v-if="showIcon">
            <CheckCircleIcon
                v-if="type === 'success'"
                class="w-5 h-5 text-green-600"
            />
            <InformationCircleIcon
                v-if="type === 'info'"
                class="w-5 h-5 text-blue-600"
            />
            <ExclamationCircleIcon
                v-if="type === 'warning'"
                class="w-5 h-5 text-yellow-600"
            />
            <ExclamationTriangleIcon
                v-if="type === 'danger'"
                class="w-5 h-5 text-red-600"
            />
        </slot>
        <div
            class="flex flex-col md:flex-row gap-4 md:gap-6 justify-between text-sm font-medium flex-1"
        >
            <div class="w-full">
                <slot></slot>
            </div>
            <div
                class="flex gap-2 items-center"
                v-if="(ctaText && ctaUrl) || dismissable"
            >
                <a
                    v-if="ctaText && ctaUrl"
                    :href="ctaUrl"
                    class="button x-small border-transparent bg-slate-900 text-white hover:bg-blue-700 hover:text-white hover:border-transparent shadow-none whitespace-nowrap"
                    :target="ctaNewWindow ? '_blank' : '_self'"
                >
                    {{ ctaText }}
                </a>
                <div v-if="dismissable" class="flex items-center">
                    <button
                        @click="dismiss"
                        class="p-0.5 rounded-md flex items-center bg-transparent shadow-none border-slate-900 hover:bg-gray-900 hover:text-white hover:border-transparent"
                        aria-label="Dismiss Banner"
                    >
                        <XMarkIcon class="h-6 w-6" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { XMarkIcon } from "@heroicons/vue/24/solid";
import { useLocalStorage } from "@vueuse/core";
import {
    CheckCircleIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
} from "@heroicons/vue/20/solid";
import { computed } from "vue";

function parseLocalDate(dateString?: string): Date | null {
    if (!dateString) return null;
    // Parse 'YYYY-MM-DD' as local date
    const [year, month, day] = dateString.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
}

function toDateOnly(date: Date | null): Date | null {
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

const isVisible = computed(() => {
    const today = toDateOnly(new Date())!; // toDateOnly will never return null for new Date()
    const start = props.startDate
        ? toDateOnly(parseLocalDate(props.startDate))
        : null;
    const end = props.endDate
        ? toDateOnly(parseLocalDate(props.endDate))
        : null;

    const isAfterStart = !start || today >= start;
    const isBeforeEnd = !end || today <= end;

    return isAfterStart && isBeforeEnd;
});

interface AlertProps {
    alertName: any;
    type: string;
    showIcon?: boolean;
    dismissable?: boolean;
    floating?: boolean;
    startDate?: string;
    endDate?: string;
    ctaText?: string;
    ctaUrl?: string;
    ctaNewWindow?: boolean;
}

const props = withDefaults(defineProps<AlertProps>(), {
    alertName: "alert-banner",
    type: "info",
    showIcon: true,
    dismissable: false,
    floating: false,
});
const alertStorage = useLocalStorage("clp/banners/alert", {
    [props.alertName]: false,
});

function dismiss() {
    alertStorage.value[props.alertName] = true;
}
</script>
